import React from 'react'

import { SigurPopover } from '../lib/SigurPopover'

const COLOR = {
  gray: '#9ca3af',
  yellow: '#facc15',
  green: '#22c55e',
  red: '#ef4444',
}

function formatDateTimeRu(ts) {
  if (!ts) return null
  const d = ts instanceof Date ? ts : new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function tooltipTextForColor(color) {
  if (color === 'green') return 'Этап согласования пройден'
  if (color === 'yellow') return 'Этап согласования в процессе'
  if (color === 'red') return 'Заявка не согласована'
  return 'Этап согласования не начат'
}

function statusTypeFromCode(approvalFlowStatus) {
  if (!approvalFlowStatus || typeof approvalFlowStatus !== 'string') return null
  return approvalFlowStatus.split(':')[0]
}

function renderMetaTs(ts) {
  if (!ts) return null
  const formattedTs = formatDateTimeRu(ts)
  if (!formattedTs) return null
  // Для схемы используем свой стиль без margin-bottom,
  // чтобы выравнивание было ближе к макету ТЗ.
  return <div className="approval-scheme__meta-ts">{formattedTs}</div>
}

export default function ApprovalProcessScheme({ scheme, getChoice }) {
  const [hoverTooltipKey, setHoverTooltipKey] = React.useState(null)
  const [expandedLevels, setExpandedLevels] = React.useState(() => new Set())

  const handleItemMouseLeave = (e) => {
    const rt = e && e.relatedTarget
    if (rt && typeof rt.closest === 'function') {
      const el = rt.closest('[data-approval-tooltip-key]')
      if (el) {
        setHoverTooltipKey(el.getAttribute('data-approval-tooltip-key') || null)
      } else {
        setHoverTooltipKey(null)
      }
    } else {
      setHoverTooltipKey(null)
    }
  }

  const statusCode = scheme?.approval_flow_status
  const statusType = statusTypeFromCode(statusCode)
  const isWaitingRequestFix = statusType === 'waiting_request_fix'
  const routeStatusType = isWaitingRequestFix ? 'in_progress' : statusType
  const currentLevel = Number(scheme?.current_level) || 1

  const steps = Array.isArray(scheme?.route_steps) ? scheme.route_steps : []
  const stepColorById = new Map()

  for (const st of steps) {
    let c = 'gray'
    if (st.completed) {
      c = 'green'
    } else if (routeStatusType === 'in_progress' && Number(st.level) === currentLevel) {
      c = isWaitingRequestFix ? 'green' : 'yellow'
    }
    stepColorById.set(st.step_id, c)
  }

  const stepsByLevel = steps
    .slice()
    .sort((a, b) => Number(a.level) - Number(b.level))
    .reduce((acc, st) => {
      const lv = st.level
      if (!acc.has(lv)) acc.set(lv, [])
      acc.get(lv).push(st)
      return acc
    }, new Map())

  const levels = Array.from(stepsByLevel.keys()).sort((a, b) => Number(a) - Number(b))

  // По клику раскрываем/сворачиваем вложенные шаги уровня (Type1).
  const levelsKey = levels.join('|')
  React.useEffect(() => {
    // По умолчанию свернуто: expandedLevels должен оставаться пустым,
    // а при смене уровней просто отфильтруем "развернутые" под текущую схему.
    const allowed = new Set(levels)
    setExpandedLevels((prev) => {
      const next = new Set()
      for (const lv of prev) {
        if (allowed.has(lv)) next.add(lv)
      }
      return next
    })
  }, [levelsKey])

  if (!scheme || !scheme.approval_flow_status) return null

  const type2ByLevel = new Map()
  for (const lv of levels) {
    const sts = stepsByLevel.get(lv) || []
    const colors = sts.map((s) => stepColorById.get(s.step_id))
    const allGreen = colors.every((c) => c === 'green')
    const anyYellow = colors.some((c) => c === 'yellow')
    const color = allGreen ? 'green' : anyYellow ? 'yellow' : 'gray'
    const maxDecidedAt = sts.reduce((mx, s) => {
      const t = s.decided_at ? new Date(s.decided_at).getTime() : null
      if (t == null) return mx
      return mx == null ? t : Math.max(mx, t)
    }, null)
    const earliestActivityAt = sts.reduce((mn, s) => {
      const times = [s.decided_at, s.taken_at]
        .filter(Boolean)
        .map((x) => new Date(x).getTime())
        .filter((t) => Number.isFinite(t))
      if (!times.length) return mn
      const stepMin = Math.min(...times)
      return mn == null ? stepMin : Math.min(mn, stepMin)
    }, null)

    type2ByLevel.set(lv, {
      level: lv,
      color,
      maxDecidedAt,
      earliestActivityAt,
      tooltip: tooltipTextForColor(color),
      steps: sts,
    })
  }

  let type3Color = 'gray'
  if (statusType === 'not_taken') type3Color = 'yellow'
  else if (statusType === 'refused' || statusType === 'request_cancelled') type3Color = 'red'
  else if (statusType === 'approved_full' || statusType === 'approved_partial') type3Color = 'green'
  else if (statusType === 'in_progress') type3Color = 'gray'
  else type3Color = 'gray'

  const type3Timestamp =
    statusType === 'request_cancelled'
      ? scheme.request_cancelled_at
      : type3Color === 'red'
        ? scheme.refused_at
        : type3Color === 'green'
          ? scheme.approved_at
          : null

  const statusLabel = getChoice ? getChoice('ApprovalFlowStatus', statusCode) : statusCode
  const notTakenStatusLabel = getChoice ? getChoice('ApprovalFlowStatus', 'not_taken') : 'Ожидает принятия'

  const inProgressNums = (() => {
    // statusCode формат: in_progress:<n>:<m>
    if (typeof statusCode === 'string' && statusCode.startsWith('in_progress:')) {
      const parts = statusCode.split(':')
      if (parts.length === 3) {
        const n = Number(parts[1])
        const m = Number(parts[2])
        if (Number.isFinite(n) && Number.isFinite(m) && n > 0 && m > 0) {
          return { n, m }
        }
      }
    }

    const n = Number(scheme.current_level)
    const m = scheme.max_level != null ? Number(scheme.max_level) : n
    if (Number.isFinite(n) && Number.isFinite(m) && n > 0 && m > 0) {
      return { n, m }
    }
    return { n: null, m: null }
  })()

  const inProgressFallbackLabel = (() => {
    // statusCode формат: in_progress:<n>:<m>
    if (typeof statusCode === 'string' && statusCode.startsWith('in_progress:')) {
      const parts = statusCode.split(':')
      if (parts.length === 3) {
        const n = Number(parts[1])
        const m = Number(parts[2])
        if (Number.isFinite(n) && Number.isFinite(m) && n > 0 && m > 0) {
          return `Принято в работу (${n} из ${m})`
        }
      }
    }

    const n = Number(scheme.current_level)
    const m = scheme.max_level != null ? Number(scheme.max_level) : n
    if (Number.isFinite(n) && n > 0 && Number.isFinite(m) && m > 0) {
      return `Принято в работу (${n} из ${m})`
    }

    return 'Принято в работу'
  })()

  const getApprovedPartialLabel = ((statusLabel) => {
    const n = scheme.approved_cnt
    const m = scheme.refused_cnt

    return `Согласовано частично (${n} из ${n+m})`
  })

  const inProgressDotColor =
    inProgressNums.n != null && inProgressNums.m != null && inProgressNums.n >= inProgressNums.m ? 'green' : 'yellow'

  // После «Не принято в работу» этап «Принято в работу» считается пройденным (зелёный кружок),
  // пока идёт in_progress — жёлтый, пока n < m.
  const inProgressRowDotColor =
    statusType === 'in_progress' ? inProgressDotColor : isWaitingRequestFix || statusType !== 'not_taken' ? 'green' : 'gray'

  const inProgressRowLabel = isWaitingRequestFix
    ? inProgressFallbackLabel
    : statusType === 'in_progress'
      ? statusLabel || inProgressFallbackLabel
      : inProgressFallbackLabel
  const approvedPartialLabel =  statusType === 'approved_partial' ? getApprovedPartialLabel(statusLabel) : statusLabel
  const showInProgressStep = statusType != null && statusType !== 'not_taken'

  const finalStatutes = new Set([
    'approved_full',
    'approved_partial',
    'refused',
    'request_cancelled'
  ]);

  const isFinalizedStatus = finalStatutes.has(statusType)

  // При «Не согласовано» / «Заявка отменена» не показываем этапы, не начатые (серый) и в процессе (жёлтый).
  const hideGrayYellowRows =
    statusType === 'refused' || statusType === 'request_cancelled'
  const isRowVisible = (color) =>
    !hideGrayYellowRows || (color !== 'gray' && color !== 'yellow')

  const visibleLevels = hideGrayYellowRows
    ? levels.filter((lv) => {
        const node = type2ByLevel.get(lv)
        return node && isRowVisible(node.color)
      })
    : levels

  const waitingRequestFixLabel = getChoice
    ? getChoice('ApprovalFlowStatus', 'waiting_request_fix')
    : 'Ожидает правок по заявке'
  const waitingRequestFixTimestamp = scheme?.waiting_request_fix_at || null

  // Индикатор «Не принято в работу» на схеме должен оставаться,
  // но как только согласование пошло дальше (in_progress/approved/refused) — считается пройденным.
  const notTakenDotColor = statusType === 'not_taken' ? 'yellow' : 'green'
  // Дата у первой строки — появление согласования (ожидание); у «Принято в работу» — in_progress_at, иначе совпадали бы.
  const notTakenTimestamp =
    scheme.approval_created_at ||
    (statusType !== 'not_taken' ? scheme.in_progress_at || null : null)

  const earliestActivityAmongStepsMs = steps.reduce((mn, s) => {
    const times = [s.decided_at, s.taken_at]
      .filter(Boolean)
      .map((x) => new Date(x).getTime())
      .filter((t) => Number.isFinite(t))
    if (!times.length) return mn
    const stepMin = Math.min(...times)
    return mn == null ? stepMin : Math.min(mn, stepMin)
  }, null)
  const inProgressRowTimestamp =
    scheme.in_progress_at ||
    (inProgressRowDotColor === 'yellow' && earliestActivityAmongStepsMs != null
      ? new Date(earliestActivityAmongStepsMs)
      : null)

  const renderType1 = (st, indentPx = 36) => {
    const color = stepColorById.get(st.step_id) || 'gray'
    if (!isRowVisible(color)) return null
    const ts =
      color === 'green' || color === 'red'
        ? st.decided_at
        : color === 'yellow'
          ? st.decided_at || st.taken_at || null
          : null
    const tooltipKey = `type1:${st.step_id}`
    return (
      <div
        key={st.step_id}
        className="approval-scheme__item approval-scheme__item--type1"
        style={{ marginLeft: indentPx }}
        data-approval-tooltip-key={tooltipKey}
        onMouseEnter={() => setHoverTooltipKey(tooltipKey)}
        onMouseLeave={handleItemMouseLeave}
      >
        <div className="approval-scheme__row">
          <span className="approval-scheme__dot" style={{ background: COLOR[color] }} />
          <SigurPopover
            placement="right"
            className="approval-scheme__popover"
            visible={hoverTooltipKey === tooltipKey}
            trigger={
              <div className={`approval-scheme__content ${ts ? '' : 'approval-scheme__content--no-meta'}`}>
                {renderMetaTs(ts)}
                <div className="sigur-timeline__title">{st.name || '---'}</div>
              </div>
            }
          >
            {tooltipTextForColor(color)}
          </SigurPopover>
        </div>
      </div>
    )
  }

  const renderType2 = (lv, indentPx = 18) => {
    const node = type2ByLevel.get(lv)
    if (!node) return null
    const color = node.color
    if (!isRowVisible(color)) return null
    const ts =
      color === 'green' || color === 'red'
        ? node.maxDecidedAt != null
          ? new Date(node.maxDecidedAt)
          : null
        : color === 'yellow' && node.earliestActivityAt != null
          ? new Date(node.earliestActivityAt)
          : null
    const label = `Уровень согласования ${lv}`
    const tooltipKey = `type2:${lv}`
    const expanded = expandedLevels.has(lv)

    return (
      <div
        key={lv}
        className="approval-scheme__item approval-scheme__item--type2"
        style={{ marginLeft: indentPx }}
        data-approval-tooltip-key={tooltipKey}
        onMouseEnter={() => setHoverTooltipKey(tooltipKey)}
        onMouseLeave={handleItemMouseLeave}
      >
        <div className="approval-scheme__row">
          <span className="approval-scheme__dot" style={{ background: COLOR[color] }} />
          <SigurPopover
            placement="right"
            className="approval-scheme__popover"
            visible={hoverTooltipKey === tooltipKey}
            trigger={
              <div className={`approval-scheme__content ${ts ? '' : 'approval-scheme__content--no-meta'}`}>
                {renderMetaTs(ts)}
                <div
                  className="sigur-timeline__title"
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpandedLevels((prev) => {
                      const next = new Set(prev)
                      if (next.has(lv)) next.delete(lv)
                      else next.add(lv)
                      return next
                    })
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setExpandedLevels((prev) => {
                        const next = new Set(prev)
                        if (next.has(lv)) next.delete(lv)
                        else next.add(lv)
                        return next
                      })
                    }
                  }}
                >
                  <span
                    className={`approval-scheme__expand-icon ${expanded ? 'approval-scheme__expand-icon--expanded' : ''}`}
                    aria-hidden="true"
                  />
                  {label}
                </div>
              </div>
            }
          >
            {node.tooltip}
          </SigurPopover>
        </div>
        {expanded ? (
          <div className="approval-scheme__type1-list">
            {node.steps
              .map((st) => renderType1(st, indentPx + 18))
              .filter(Boolean)}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="approval-scheme">
      {/* Всегда показываем начало процесса (первую строку из ТЗ) */}
      {isRowVisible(notTakenDotColor) ? (
      <div
        className="approval-scheme__item approval-scheme__item--type3"
        style={{ marginLeft: 0 }}
        data-approval-tooltip-key="type3:not_taken"
        onMouseEnter={() => setHoverTooltipKey('type3:not_taken')}
        onMouseLeave={handleItemMouseLeave}
      >
        <div className="approval-scheme__row">
          <span className="approval-scheme__dot" style={{ background: COLOR[notTakenDotColor] }} />
          <SigurPopover
            placement="right"
            className="approval-scheme__popover"
            visible={hoverTooltipKey === 'type3:not_taken'}
            trigger={
              <div className={`approval-scheme__content ${notTakenTimestamp ? '' : 'approval-scheme__content--no-meta'}`}>
                {renderMetaTs(notTakenTimestamp)}
                <div className="sigur-timeline__title">{notTakenStatusLabel || 'Не принято в работу'}</div>
              </div>
            }
          >
            {tooltipTextForColor(notTakenDotColor)}
          </SigurPopover>
        </div>
      </div>
      ) : null}

      {/* 2) «Принято в работу» — при in_progress и после завершения маршрута (approved/refused), пока не not_taken */}
      {showInProgressStep && isRowVisible(inProgressRowDotColor) ? (
        <div
          className="approval-scheme__item approval-scheme__item--type3"
          style={{ marginLeft: 0 }}
          data-approval-tooltip-key="type3:in_progress"
          onMouseEnter={() => setHoverTooltipKey('type3:in_progress')}
          onMouseLeave={handleItemMouseLeave}
        >
          <div className="approval-scheme__row">
            <span className="approval-scheme__dot" style={{ background: COLOR[inProgressRowDotColor] }} />
            <SigurPopover
              placement="right"
              className="approval-scheme__popover"
              visible={hoverTooltipKey === 'type3:in_progress'}
              trigger={
                <div
                  className={`approval-scheme__content ${inProgressRowTimestamp ? '' : 'approval-scheme__content--no-meta'}`}
                >
                  {renderMetaTs(inProgressRowTimestamp || null)}
                  <div className="sigur-timeline__title">{inProgressRowLabel}</div>
                </div>
              }
            >
              {tooltipTextForColor(inProgressRowDotColor)}
            </SigurPopover>
          </div>
        </div>
      ) : null}

      {routeStatusType !== 'not_taken' && visibleLevels.length > 0 ? (
        <div className="approval-scheme__levels">
          {visibleLevels.map((lv) => renderType2(lv, 18)).filter(Boolean)}
        </div>
      ) : null}

      {isWaitingRequestFix && isRowVisible('yellow') ? (
        <div
          className="approval-scheme__item approval-scheme__item--type3"
          style={{ marginLeft: 0 }}
          data-approval-tooltip-key="type3:waiting_request_fix"
          onMouseEnter={() => setHoverTooltipKey('type3:waiting_request_fix')}
          onMouseLeave={handleItemMouseLeave}
        >
          <div className="approval-scheme__row">
            <span className="approval-scheme__dot" style={{ background: COLOR.yellow }} />
            <SigurPopover
              placement="right"
              className="approval-scheme__popover"
              visible={hoverTooltipKey === 'type3:waiting_request_fix'}
              trigger={
                <div
                  className={`approval-scheme__content ${waitingRequestFixTimestamp ? '' : 'approval-scheme__content--no-meta'}`}
                >
                  {renderMetaTs(waitingRequestFixTimestamp)}
                  <div className="sigur-timeline__title">{waitingRequestFixLabel || 'Ожидает правок по заявке'}</div>
                </div>
              }
            >
              {tooltipTextForColor('yellow')}
            </SigurPopover>
          </div>
        </div>
      ) : null}

      {isFinalizedStatus && isRowVisible(type3Color) ? (
        <div
          className="approval-scheme__item approval-scheme__item--type3"
          style={{ marginLeft: 0 }}
          data-approval-tooltip-key="type3:final"
          onMouseEnter={() => setHoverTooltipKey('type3:final')}
          onMouseLeave={handleItemMouseLeave}
        >
          <div className="approval-scheme__row">
            <span className="approval-scheme__dot" style={{ background: COLOR[type3Color] }} />
            <SigurPopover
              placement="right"
              className="approval-scheme__popover"
              visible={hoverTooltipKey === 'type3:final'}
              trigger={
                <div className={`approval-scheme__content ${type3Timestamp ? '' : 'approval-scheme__content--no-meta'}`}>
                  {renderMetaTs(type3Timestamp || null)}
                  <div className="sigur-timeline__title">{approvedPartialLabel || '---'}</div>
                </div>
              }
            >
              {tooltipTextForColor(type3Color)}
            </SigurPopover>
          </div>
        </div>
      ) : null}
    </div>
  )
}

